// @ts-nocheck

import { supabase } from '@/lib/supabase';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface AffiliateClickMetadata {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  source_url?: string;
  user_agent?: string;
}

const generateSessionId = () => crypto.randomUUID();

interface AffiliateData {
  affiliateId: string;
  commissionRate: number;
}

interface AffiliateEarningData {
  affiliate_id: string;
  amount: number;
  commission_rate: number;
  source_type: string;
  source_id: string | null;
  status: string;
}

// Validate referral code and return affiliate data (supports links and direct codes)
export const validateReferralCode = async (referralCode: string): Promise<AffiliateData | null> => {
  try {
    const code = referralCode.toUpperCase();
    // Try direct affiliate.referral_code first
    const { data: affiliate, error } = await supabase
      .from('affiliates')
      .select('id, commission_rate, status')
      .eq('referral_code', code)
      .eq('status', 'active')
      .maybeSingle();

    if (affiliate) {
      return { affiliateId: affiliate.id, commissionRate: affiliate.commission_rate };
    }

    // Fallback: check affiliate_referral_links
    const { data: link, error: linkError } = await supabase
      .from('affiliate_referral_links')
      .select('affiliate_id')
      .eq('code', code)
      .maybeSingle();

    if (linkError) {
      console.warn('❌ [AFFILIATE-TRACKING] Referral link lookup error:', linkError.message);
    }

    if (link?.affiliate_id) {
      // Fetch commission rate from affiliates
      const { data: aff2, error: aff2Error } = await supabase
        .from('affiliates')
        .select('id, commission_rate, status')
        .eq('id', link.affiliate_id)
        .eq('status', 'active')
        .single();
      if (!aff2Error && aff2) {
        return { affiliateId: aff2.id, commissionRate: aff2.commission_rate };
      }
    }

    console.warn('❌ [AFFILIATE-TRACKING] Invalid or inactive referral code:', code);
    return null;
  } catch (error) {
    console.error('❌ [AFFILIATE-TRACKING] Error validating referral code:', error);
    return null;
  }
};

// Store referral in session storage
export const storeReferralInSession = (referralCode: string, sourceUrl: string) => {
  const referralData = {
    code: referralCode,
    sourceUrl: sourceUrl,
    timestamp: new Date().toISOString()
  };
  
  sessionStorage.setItem('affiliate_referral', JSON.stringify(referralData));
  console.log('✅ [AFFILIATE-TRACKING] Referral stored in session:', referralData);
};

// Get referral from session storage
export const getReferralFromSession = (): { code: string; sourceUrl: string; timestamp: string } | null => {
  try {
    const referralData = sessionStorage.getItem('affiliate_referral');
    if (referralData) {
      return JSON.parse(referralData);
    }
  } catch (error) {
    console.error('❌ [AFFILIATE-TRACKING] Error getting referral from session:', error);
  }
  return null;
};

// Clear referral from session storage
export const clearReferralFromSession = () => {
  sessionStorage.removeItem('affiliate_referral');
  console.log('✅ [AFFILIATE-TRACKING] Referral cleared from session');
};

// Get just the stored referral code (if any)
export const getStoredReferralCode = (): string | null => {
  try {
    const local = typeof window !== 'undefined' ? localStorage.getItem('affiliateReferralCode') : null;
    if (local) return local;
  } catch {}
  const data = getReferralFromSession();
  return data?.code ?? null;
};

// Get permanently stored referred affiliate id from the user's profile
export const getReferredAffiliateIdForUser = async (userId: string): Promise<string | null> => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('referred_by_affiliate_id')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      console.warn('⚠️ [AFFILIATE-TRACKING] Error fetching referred_by_affiliate_id:', error.message);
      return null;
    }

    return data?.referred_by_affiliate_id ?? null;
  } catch (e) {
    console.error('❌ [AFFILIATE-TRACKING] Unexpected error fetching referred affiliate id:', e);
    return null;
  }
};

// Record affiliate earning with duplicate prevention
export const recordAffiliateEarning = async (earningData: AffiliateEarningData): Promise<boolean> => {
  try {
    // Check for existing earning record to prevent duplicates
    const { data: existingEarning, error: checkError } = await supabase
      .from('affiliate_earnings')
      .select('id')
      .eq('affiliate_id', earningData.affiliate_id)
      .eq('source_type', earningData.source_type)
      .eq('source_id', earningData.source_id)
      .eq('status', 'pending')
      .maybeSingle();
      
    if (checkError && checkError.code !== 'PGRST116') {
      console.error('❌ [AFFILIATE-TRACKING] Error checking existing earning:', checkError);
      return false;
    }
    
    if (existingEarning) {
      console.log('⚠️ [AFFILIATE-TRACKING] Earning already exists, skipping duplicate:', {
        affiliateId: earningData.affiliate_id,
        sourceType: earningData.source_type,
        sourceId: earningData.source_id
      });
      return true; // Return true since earning is already recorded
    }

    // Get current session referral info
    const referralInfo = getReferralFromSession();
    
    // Run fraud detection before recording earning
    const fraudResult = await runFraudDetection({
      affiliate_id: earningData.affiliate_id,
      user_id: '', // We don't have user_id in this context
      user_email: '', // We don't have user_email in this context
      user_ip: '', // We don't have user_ip in this context
      amount: earningData.amount,
      source: earningData.source_type
    });

    // Determine earning status based on fraud result
    let status = earningData.status;
    if (fraudResult.action === 'block') {
      status = 'blocked';
    } else if (fraudResult.action === 'manual_review') {
      status = 'under_review';
    }
    
    // Insert new earning record
    const { error: insertError } = await supabase
      .from('affiliate_earnings')
      .insert({
        affiliate_id: earningData.affiliate_id,
        amount: earningData.amount,
        commission_rate: earningData.commission_rate,
        source_type: earningData.source_type,
        source_id: earningData.source_id,
        status: status,
        earned_at: new Date().toISOString(),
        source_url: referralInfo?.sourceUrl
      });
      
    if (insertError) {
      console.error('❌ [AFFILIATE-TRACKING] Error recording affiliate earning:', insertError);
      return false;
    }
    
    console.log('✅ [AFFILIATE-TRACKING] Affiliate earning recorded with fraud check:', earningData, 'Status:', status);
    return true;
  } catch (error) {
    console.error('❌ [AFFILIATE-TRACKING] Unexpected error recording affiliate earning:', error);
    return false;
  }
};

// Run fraud detection on conversion
export const runFraudDetection = async (conversionData: {
  affiliate_id: string;
  user_id: string;
  user_email: string;
  user_ip: string;
  amount: number;
  source: string;
}): Promise<any> => {
  try {
    const { data, error } = await invokeEdgeFunction('affiliate-fraud-detection', {
      body: conversionData
    });

    if (error) {
      console.error('❌ [AFFILIATE-TRACKING] Fraud detection error:', error);
      return { action: 'approve', is_suspicious: false, flags: [] };
    }

    return data;
  } catch (error) {
    console.error('❌ [AFFILIATE-TRACKING] Failed to run fraud detection:', error);
    return { action: 'approve', is_suspicious: false, flags: [] };
  }
};

// Track affiliate click via Edge Function
export const trackAffiliateClick = async (referralCode: string, metadata: AffiliateClickMetadata = {}) => {
  try {
    const timestamp = Date.now();
    const sessionId = generateSessionId();
    
    console.log('🔍 Tracking affiliate click:', { referralCode, metadata, timestamp });
    
    // Store in localStorage for attribution tracking
    const clickData = {
      referralCode,
      timestamp,
      sessionId,
      ...metadata
    };
    
    localStorage.setItem('affiliate_click', JSON.stringify(clickData));
    localStorage.setItem('affiliate_referral_code', referralCode);
    
    // Set expiration (30 days)
    const expirationTime = timestamp + (30 * 24 * 60 * 60 * 1000);
    localStorage.setItem('affiliate_click_expires', expirationTime.toString());
    
    // Track the click via edge function
    const { data, error } = await invokeEdgeFunction('affiliate-track-click', {
      body: {
        referral_code: referralCode,
        metadata: {
          ...metadata,
          session_id: sessionId,
          timestamp
        }
      }
    });

    if (error) {
      console.error('❌ Click tracking error:', error);
      return false;
    }

    console.log('✅ Click tracked successfully:', data);
    
    // Create event for audit trail
    if (data?.affiliate_id) {
      await supabase.rpc('create_affiliate_event', {
        p_event_type: 'click',
        p_entity_id: data.click_id || crypto.randomUUID(),
        p_entity_type: 'click',
        p_event_data: {
          affiliate_id: data.affiliate_id,
          referral_code: referralCode,
          click_metadata: metadata,
          session_id: sessionId
        }
      });
    }
    
    return true;
  } catch (error) {
    console.error('❌ Affiliate click tracking failed:', error);
    return false;
  }
};
